import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { signUp } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import awsConfig from '../../../amplify_outputs.json';
import TextFieldAuth from '../../../components/custom/TextFieldAuth';
import { UserRole } from '../../../utils/types/types';
import ConfirmationSent from './ConfirmationSent';

interface SignUpFormValues {
  companyName: string;
  userId: string;
  companyRegistrationNumber: string;
  authorizedSignatoryName: string;
  authorizedSignatoryPhone: string;
  email: string;
  password: string;
}

const client = generateClient<Schema>();

const SignUp: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatPhoneNumberToE164 = (phone: string): string => {
    if (phone.startsWith('0')) {
      return `+972${phone.slice(1)}`;
    }
    return phone;
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const [ads, setAds] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const {
    handleSubmit,
    control,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>();

  const email = watch('email');

  const onSubmit = async (data: SignUpFormValues) => {
    console.log('SignUp onSubmit called with data:', data);
    setIsLoading(true);

    const formattedPhone = formatPhoneNumberToE164(
      data.authorizedSignatoryPhone,
    );

    try {
      const signUpResponse = await signUp({
        username: data.email,
        password: data.password,
        options: {
          userAttributes: {
            email: data.email,
            phone_number: formattedPhone,
            name: data.authorizedSignatoryName,
            'custom:role': UserRole.CLIENT,
          },
          clientMetadata: {
            confirmationUrl: awsConfig.custom.confirmVerificationFunctionUrl,
          },
        },
      });

      const userResponse = await client.models.User.create({
        id: signUpResponse.userId,
        userId: data.userId,
        companyName: data.companyName || null,
        companyRegistrationNumber: data.companyRegistrationNumber || '',
        authorizedSignatoryName: data.authorizedSignatoryName || null,
        authorizedSignatoryPhone: data.authorizedSignatoryPhone || '',
        email: data.email || '',
        phone: data.authorizedSignatoryPhone || '',
        role: UserRole.CLIENT,
        paymentPlanId: '846ed772-f3e8-40b0-9da2-02c8d607994a',
        ads: ads,
        createdAt: new Date().toISOString(),
      });

      console.log('userResponse', userResponse);

      setConfirmationDialogOpen(true);
    } catch (error: any) {
      console.error('SignUp error:', error);
      setError('email', {
        type: 'manual',
        message: error.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setConfirmationDialogOpen(false);
    navigate('/login');
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {!isMobile ? (
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: '25px',
            background: 'transparent',
            my: '20px',
            gap: '18px',
            borderRadius: '20px',
            maxWidth: '600px',
          }}
          autoComplete="on"
        >
          <Box
            component="img"
            src="/logo.svg"
            sx={{
              alignSelf: 'flex-end',
              maxWidth: '300px',
              height: 'auto',
            }}
          />
          <Stack justifyContent={'flex-end'}>
            <Typography
              fontWeight={'800'}
              fontSize={'62px'}
              fontFamily={'Heebo'}
              textAlign={'right'}
              color={theme.palette.white.main}
              dir="rtl"
            >
              ברוכים הבאים!
            </Typography>
            <Typography
              fontWeight={'300'}
              fontSize={'32px'}
              fontFamily={'Heebo'}
              textAlign={'right'}
              color={theme.palette.white.main}
              dir="rtl"
            >
              הופכים את תהליך הפקדות הפנסיה לפשוט ויעיל מאי פעם!
            </Typography>
            <Typography
              fontWeight={'300'}
              fontSize={'25px'}
              fontFamily={'Heebo'}
              textAlign={'right'}
              color={theme.palette.white.main}
              dir="rtl"
            >
              מדי חודש, תקבלו לוואטסאפ את כל התשלומים לאישור מהיר - אנחנו נדאג
              לכל השאר
            </Typography>
          </Stack>
          <Stack
            width={'100%'}
            direction="row"
            justifyContent={'space-between'}
            spacing={4}
          >
            <Grid
              container
              spacing={3.5}
              width={'100%'}
              direction="row-reverse"
              justifyContent="flex-end"
            >
              <Grid item xs={12} md={6}>
                <Controller
                  name="companyName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="companyName"
                      label="שם העסק"
                      fullWidth
                      error={!!errors.companyName}
                      helperText={
                        errors.companyName ? errors.companyName.message : ''
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="companyRegistrationNumber"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'מספר ח.פ. אינו תקין',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="ח.פ."
                      label="ח.פ."
                      fullWidth
                      required
                      error={!!errors.companyRegistrationNumber}
                      helperText={
                        errors.companyRegistrationNumber
                          ? errors.companyRegistrationNumber.message
                          : ''
                      }
                      onBlur={() => trigger('companyRegistrationNumber')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <Stack sx={{ mb: '-15px', mt: '10px' }}>
                  <Typography
                    color="#fff"
                    fontWeight={700}
                    fontSize="18px"
                    fontFamily="Heebo"
                    dir="rtl"
                    sx={{ textAlign: 'right' }}
                  >
                    פרטי מורשה חתימה:
                  </Typography>
                  <Typography
                    color="#fff"
                    fontWeight={700}
                    fontSize="12px"
                    fontFamily="Heebo"
                    dir="rtl"
                    sx={{ textAlign: 'right' }}
                  >
                    נא למלא את פרטי המורשה לחתום בשם העסק.
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="authorizedSignatoryName"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^[a-zA-Zא-ת]+\s[a-zA-Zא-ת\s]+$/,
                      message:
                        'מלאו את שמכם המלא עם רווח בין השם הפרטי לשם המשפחה',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="authorizedSignatoryName"
                      label="שם מלא"
                      fullWidth
                      error={!!errors.authorizedSignatoryName}
                      helperText={
                        errors.authorizedSignatoryName
                          ? errors.authorizedSignatoryName.message
                          : ''
                      }
                      onBlur={() => trigger('authorizedSignatoryName')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="userId"
                  control={control}
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'נא להזין ספרות בלבד',
                    },
                    minLength: {
                      value: 9,
                      message: 'תעודת זהות חייבת להכיל 9 ספרות',
                    },
                    maxLength: {
                      value: 9,
                      message: 'תעודת זהות חייבת להכיל 9 ספרות',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="ת״ז"
                      label="ת״ז"
                      fullWidth
                      required
                      autoComplete="off"
                      error={!!errors.userId}
                      helperText={errors.userId ? errors.userId.message : ''}
                      onBlur={() => trigger('userId')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="authorizedSignatoryPhone"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^(0\d{8,9}|\+972\d{8,9})$/,
                      message: 'נא למלא מספר טלפון תקין',
                    },
                    minLength: {
                      value: 9,
                      message: 'נא למלא לפחות 9 תווים',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="authorizedSignatoryPhone"
                      label="טלפון"
                      fullWidth
                      required
                      error={!!errors.authorizedSignatoryPhone}
                      helperText={
                        errors.authorizedSignatoryPhone
                          ? errors.authorizedSignatoryPhone.message
                          : ''
                      }
                      onBlur={() => trigger('authorizedSignatoryPhone')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'כתובת המייל אינה תקינה',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="email"
                      label="מייל"
                      fullWidth
                      required
                      autoComplete="email"
                      error={!!errors.email}
                      helperText={errors.email ? errors.email.message : ''}
                      onBlur={() => trigger('email')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    minLength: {
                      value: 8,
                      message: 'הסיסמה חייבת להכיל לפחות 8 תווים',
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
                      message:
                        'הסיסמה צריכה להכיל לפחות 8 תווים ולהכיל שילוב של אות גדולה, אות קטנה וספרות',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="password"
                      label="יצירת סיסמה"
                      fullWidth
                      required
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="password"
                      error={!!errors.password}
                      helperText={
                        errors.password ? errors.password.message : ''
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClickShowPassword}>
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onBlur={() => trigger('password')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6} />
            </Grid>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              mt: '7px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                sx={{
                  width: '52px',
                  height: '52px',
                  minWidth: '52px',
                  minHeight: '52px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  backgroundColor: '#FFFFFF33',
                  padding: 0,
                  borderRadius: '8px',
                }}
                type="submit"
                disabled={isLoading}
              >
                <img
                  alt="auth"
                  src="/icons/auth.svg"
                  width={'22.3px'}
                  height={'25.36px'}
                />
              </Button>
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
                color={theme.palette.white.main}
              >
                כניסה
              </Typography>
            </Box>
          </Box>
          <Stack
            width={'100%'}
            direction="row-reverse"
            justifyContent="space-between"
          >
            <Box
              sx={{
                display: 'flex',
                textAlign: 'right',
                justifyContent: 'flex-end',
                gap: '5px',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="body1"
                  fontWeight="fontWeightMedium"
                  fontSize={16}
                  color={theme.palette.blue.main}
                >
                  ?כבר יש לך חשבון
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="fontWeightMedium"
                  fontSize={16}
                  color={theme.palette.blue.main}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    navigate('/login');
                  }}
                >
                  התחבר
                </Typography>
              </Box>
            </Box>
            <Stack spacing={1}>
              <Typography
                fontWeight="300"
                fontFamily="Heebo"
                fontSize={13}
                color={theme.palette.white.main}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                בלחיצה שלח אני מאשר את{' '}
                <Link
                  href="https://www.pielapay.co.il/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.white.main,
                    textDecoration: 'underline',
                  }}
                >
                  תנאי שימוש
                </Link>{' '}
                ו
                <Link
                  href="https://www.pielapay.co.il/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.white.main,
                    textDecoration: 'underline',
                  }}
                >
                  מדיניות הפרטיות
                </Link>
              </Typography>
              <Stack direction="row-reverse" alignItems="center" spacing={0.5}>
                <Checkbox
                  disableRipple
                  size="small"
                  sx={{
                    '&.MuiCheckbox-root': {
                      padding: 0,
                    },
                    '& .MuiSvgIcon-root': {
                      borderRadius: 0,
                      color: theme.palette.green.light,
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                      color: theme.palette.green.light,
                    },
                  }}
                  checked={ads}
                  onChange={(e) => setAds(e.target.checked)}
                />
                <Typography
                  fontWeight={'300'}
                  fontFamily={'Heebo'}
                  fontSize={13}
                  textAlign={'right'}
                  color={theme.palette.white.main}
                >
                  אני רוצה לקבל דיוור פרסומי: מבצעים, הצעות למוצרים ומידע לפני
                  כולם על שירותים חדשים
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <ConfirmationSent
            open={confirmationDialogOpen}
            email={email}
            onClose={handleDialogClose}
          />
        </Box>
      ) : (
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            pt: '25px',
            px: '40px',
          }}
        >
          <Box
            component="img"
            src="/logo.svg"
            sx={{
              maxWidth: '287px',
              height: 'auto',
              mb: '30px',
            }}
          />

          <Stack
            sx={{
              background: '#C1FE40',
              width: 'calc(100% + 80px)',
              alignItems: 'center',
              justifyContent: 'center',
              mx: '-40px',
              mb: '10px',
            }}
          >
            <Typography
              fontSize="46px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
              color="#2C2C2C"
              dir="rtl"
            >
              ברוכים הבאים!
            </Typography>
          </Stack>

          <Stack justifyContent={'flex-end'}>
            <Typography
              fontWeight={'300'}
              fontSize={'25px'}
              fontFamily={'Heebo'}
              textAlign={'center'}
              color={theme.palette.white.main}
              dir="rtl"
            >
              הופכים את תהליך הפקדות הפנסיה לפשוט ויעיל מאי פעם!
            </Typography>
            <Typography
              fontWeight={'300'}
              fontSize={'18px'}
              fontFamily={'Heebo'}
              textAlign={'center'}
              color={theme.palette.white.main}
              dir="rtl"
            >
              מדי חודש, תקבלו לווטסאפ את כל התשלומים לאישור מהיר - אנחנו נדאג
              לכל השאר
            </Typography>
          </Stack>

          <Stack spacing="25px" sx={{ width: '100%', mb: '50px' }}>
            <Grid
              container
              spacing={3.5}
              width={'100%'}
              direction="row-reverse"
              justifyContent="flex-end"
            >
              <Grid item xs={12} md={6}>
                <Controller
                  name="authorizedSignatoryName"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^[a-zA-Zא-ת]+\s[a-zA-Zא-ת\s]+$/,
                      message:
                        'מלאו את שמכם המלא עם רווח בין השם הפרטי לשם המשפחה',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="authorizedSignatoryName"
                      label="שם מלא של מורשה חתימה"
                      fullWidth
                      error={!!errors.authorizedSignatoryName}
                      helperText={
                        errors.authorizedSignatoryName
                          ? errors.authorizedSignatoryName.message
                          : ''
                      }
                      onBlur={() => trigger('authorizedSignatoryName')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="companyName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="companyName"
                      label="שם העסק"
                      fullWidth
                      error={!!errors.companyName}
                      helperText={
                        errors.companyName ? errors.companyName.message : ''
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="companyRegistrationNumber"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'מספר ח.פ. אינו תקין',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="ח.פ."
                      label="ח.פ."
                      fullWidth
                      required
                      error={!!errors.companyRegistrationNumber}
                      helperText={
                        errors.companyRegistrationNumber
                          ? errors.companyRegistrationNumber.message
                          : ''
                      }
                      onBlur={() => trigger('companyRegistrationNumber')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="userId"
                  control={control}
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'נא להזין ספרות בלבד',
                    },
                    minLength: {
                      value: 9,
                      message: 'תעודת זהות חייבת להכיל 9 ספרות',
                    },
                    maxLength: {
                      value: 9,
                      message: 'תעודת זהות חייבת להכיל 9 ספרות',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="ת״ז"
                      label="ת״ז"
                      fullWidth
                      required
                      autoComplete="off"
                      error={!!errors.userId}
                      helperText={errors.userId ? errors.userId.message : ''}
                      onBlur={() => trigger('userId')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <Controller
                  name="authorizedSignatoryPhone"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^(0\d{8,9}|\+972\d{8,9})$/,
                      message: 'נא למלא מספר טלפון תקין',
                    },
                    minLength: {
                      value: 9,
                      message: 'נא למלא לפחות 9 תווים',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="authorizedSignatoryPhone"
                      label="טלפון מורשה חתימה"
                      fullWidth
                      required
                      error={!!errors.authorizedSignatoryPhone}
                      helperText={
                        errors.authorizedSignatoryPhone
                          ? errors.authorizedSignatoryPhone.message
                          : ''
                      }
                      onBlur={() => trigger('authorizedSignatoryPhone')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'כתובת המייל אינה תקינה',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="email"
                      label="מייל"
                      fullWidth
                      required
                      autoComplete="email"
                      error={!!errors.email}
                      helperText={errors.email ? errors.email.message : ''}
                      onBlur={() => trigger('email')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'חובה למלא את השדה',
                    minLength: {
                      value: 8,
                      message: 'הסיסמה חייבת להכיל לפחות 8 תווים',
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
                      message:
                        'הסיסמה צריכה להכיל לפחות 8 תווים ולהכיל שילוב של אות גדולה, אות קטנה וספרות',
                    },
                  }}
                  render={({ field }) => (
                    <TextFieldAuth
                      {...field}
                      id="password"
                      label="יצירת סיסמה"
                      fullWidth
                      required
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="password"
                      error={!!errors.password}
                      helperText={
                        errors.password ? errors.password.message : ''
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClickShowPassword}>
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onBlur={() => trigger('password')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{
              width: '100%',
              alignItems: 'center',
              mb: '20px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                sx={{
                  width: '52px',
                  height: '52px',
                  minWidth: '52px',
                  minHeight: '52px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  backgroundColor: '#B6F829',
                  padding: 0,
                  borderRadius: '8px',
                }}
                type="submit"
                disabled={isLoading}
              >
                <img
                  alt="auth"
                  src="/icons/auth.svg"
                  width={'22.3px'}
                  height={'25.36px'}
                />
              </Button>
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
                color={theme.palette.white.main}
              >
                כניסה
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                variant="body1"
                fontWeight="fontWeightMedium"
                fontSize={16}
                color={theme.palette.blue.main}
              >
                ?כבר יש לך חשבון
              </Typography>
              <Typography
                variant="body1"
                fontWeight="fontWeightMedium"
                fontSize={16}
                textAlign="right"
                color={theme.palette.blue.main}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  navigate('/login');
                }}
              >
                התחבר
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1}>
            <Typography
              fontWeight="300"
              fontFamily="Heebo"
              fontSize={13}
              color={theme.palette.white.main}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              בלחיצה שלח אני מאשר את{' '}
              <Link
                href="https://www.pielapay.co.il/terms"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.white.main,
                  textDecoration: 'underline',
                }}
              >
                תנאי שימוש
              </Link>{' '}
              ו
              <Link
                href="https://www.pielapay.co.il/privacy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.white.main,
                  textDecoration: 'underline',
                }}
              >
                מדיניות הפרטיות
              </Link>
            </Typography>
          </Stack>
          <ConfirmationSent
            open={confirmationDialogOpen}
            email={email}
            onClose={handleDialogClose}
          />
        </Stack>
      )}
    </>
  );
};

export default SignUp;
